import { useState } from "react";
import {
  View, Text, Pressable,
  TextInput, ActivityIndicator,
} from "react-native";
import styled from "styled-components/native";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import { Link } from "expo-router";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { useMatches } from "@/hooks/useMatches";
import { useEvents } from "@/hooks/useEvents";
import { usePosts } from "@/hooks/usePosts";
import { useTeamConfig } from "@/hooks/useTeamConfig";
import { FeedSkeleton } from "@/components/Skeleton";
import { useRouter } from "expo-router";
import { createPost, deletePost, toggleLike, setDayAvailability } from "@/lib/services";
import { useMyAvailability } from "@/hooks/useMyAvailability";
import { useLikedPosts } from "@/hooks/useLikes";
import { colors, radius, font } from "@/lib/theme";
import type { AvailabilityStatus } from "@/lib/types";
import type { PostVisibility } from "@/lib/types";
import ContentWidth from "@/components/ContentWidth";
import ConfirmSheet from "@/components/ConfirmSheet";
import { useConfirm } from "@/hooks/useConfirm";

// "Peterborough Town CC - 2nd XI" → "2nd XI", "Kimbolton CC - 1st XI" → "Kimbolton CC"
function shortTeamName(full: string): string {
  const dash = full.indexOf(" - ");
  if (dash > 0) return full.slice(dash + 3).trim();
  return full;
}
function shortOpponentName(full: string): string {
  const dash = full.indexOf(" - ");
  if (dash > 0) return full.slice(0, dash).trim();
  return full;
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  } catch {
    return dateStr;
  }
}

function formatTime(timeStr: string): string {
  try {
    const [h, m] = timeStr.split(":");
    const hour = parseInt(h);
    if (hour >= 12) return `${hour === 12 ? 12 : hour - 12}:${m}pm`;
    return `${hour === 0 ? 12 : hour}:${m}am`;
  } catch {
    return timeStr;
  }
}

function timeAgo(ts: any): string {
  if (!ts?.toDate) return "";
  const diff = Date.now() - ts.toDate().getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/* ── Styled Components ── */

/* Screen styled.ScrollView replaced by KeyboardAwareScroll */

const Center = styled.View`
  flex: 1;
  background-color: ${colors.bg};
  justify-content: center;
  align-items: center;
  padding: 24px;
`;

const EmptyTitle = styled.Text`
  font-size: 20px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  margin-bottom: 8px;
`;

const EmptyText = styled.Text`
  font-size: 14px;
  color: ${colors.inkMuted};
  text-align: center;
`;

const Section = styled.View`
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 16px;
  padding-bottom: 4px;
`;

const Greeting = styled.Text`
  font-size: 14px;
  font-weight: ${font.extrabold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const EmojiText = styled.Text`
  font-size: 48px;
  margin-bottom: 16px;
`;

/* Match card */

const MatchCard = styled.Pressable`
  margin-left: 20px;
  margin-right: 20px;
  margin-top: 12px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border-width: 2px;
  border-color: ${colors.brandLight};
  overflow: hidden;
`;

const MatchCardHeader = styled.View`
  background-color: ${colors.brand50};
  padding-left: 16px;
  padding-right: 16px;
  padding-top: 10px;
  padding-bottom: 10px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const MatchCardHeaderLeft = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const Dot = styled.View`
  width: 8px;
  height: 8px;
  border-radius: 4px;
  background-color: ${colors.brand};
`;

const MatchCardLabel = styled.Text`
  font-size: 11px;
  font-weight: ${font.extrabold};
  color: ${colors.brandDark};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const MatchCardBody = styled.View`
  padding-left: 16px;
  padding-right: 16px;
  padding-top: 14px;
  padding-bottom: 14px;
`;

const MatchTitle = styled.Text`
  font-size: 16px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  letter-spacing: -0.3px;
`;

const MatchMeta = styled.Text`
  font-size: 13px;
  color: ${colors.inkSecondary};
  font-weight: ${font.medium};
  margin-top: 4px;
`;

const MatchStatus = styled.View`
  margin-top: 10px;
`;

const StatusPill = styled.View`
  background-color: ${colors.amber50};
  border-radius: ${radius.full}px;
  padding-left: 10px;
  padding-right: 10px;
  padding-top: 4px;
  padding-bottom: 4px;
  border-width: 1px;
  border-color: ${colors.amber200};
  align-self: flex-start;
`;

const StatusPillText = styled.Text`
  font-size: 11px;
  font-weight: ${font.bold};
  color: ${colors.amber};
`;

/* Compose */

const ComposeCard = styled.View<{ focused?: boolean }>`
  margin-left: 20px;
  margin-right: 20px;
  margin-top: 12px;
  margin-bottom: 4px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border-width: ${(p) => (p.focused ? 2 : 1)}px;
  border-color: ${(p) => (p.focused ? colors.brand : colors.line)};
  padding: 14px;
`;

const ComposeTop = styled.View`
  flex-direction: row;
  align-items: flex-start;
`;

const Avatar = styled.View`
  width: 34px;
  height: 34px;
  border-radius: 17px;
  background-color: ${colors.brand50};
  border-width: 1px;
  border-color: ${colors.brandLight};
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  margin-top: 2px;
`;

const AvatarText = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.brandDark};
`;

const ComposeInput = styled.TextInput`
  flex: 1;
  font-size: 15px;
  color: ${colors.ink};
  min-height: 34px;
  max-height: 120px;
  padding-top: 0px;
  padding-bottom: 0px;
  line-height: 22px;
`;

const ComposeActions = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  padding-top: 10px;
  border-top-width: 1px;
  border-top-color: ${colors.line};
`;

const PillRow = styled.View`
  flex-direction: row;
  gap: 6px;
`;

const Pill = styled.Pressable<{ active?: boolean }>`
  background-color: ${(p) => (p.active ? colors.brand : colors.bg)};
  border-radius: ${radius.full}px;
  padding-left: 10px;
  padding-right: 10px;
  padding-top: 5px;
  padding-bottom: 5px;
  border-width: 1px;
  border-color: ${(p) => (p.active ? colors.brand : colors.line)};
`;

const PillText = styled.Text<{ active?: boolean }>`
  font-size: 11px;
  font-weight: ${font.bold};
  color: ${(p) => (p.active ? "#fff" : colors.inkSecondary)};
`;

const PostBtn = styled.Pressable<{ disabled?: boolean }>`
  background-color: ${colors.brand};
  border-radius: ${radius.md}px;
  padding-left: 18px;
  padding-right: 18px;
  padding-top: 7px;
  padding-bottom: 7px;
  opacity: ${(p) => (p.disabled ? 0.4 : 1)};
`;

const PostBtnText = styled.Text`
  color: #fff;
  font-size: 13px;
  font-weight: ${font.bold};
`;

/* Posts */

const PostCard = styled.View`
  margin-left: 20px;
  margin-right: 20px;
  margin-top: 8px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border-width: 1px;
  border-color: ${colors.line};
  overflow: hidden;
`;

const PostBody = styled.View`
  padding: 14px;
`;

const AuthorRow = styled.View`
  flex-direction: row;
  align-items: center;
  margin-bottom: 10px;
`;

const AuthorAvatar = styled.View`
  width: 36px;
  height: 36px;
  border-radius: 18px;
  background-color: ${colors.brandLight};
  align-items: center;
  justify-content: center;
  margin-right: 10px;
`;

const AuthorAvatarText = styled.Text`
  font-size: 13px;
  font-weight: ${font.extrabold};
  color: ${colors.brandDark};
`;

const AuthorNameFlex = styled.View`
  flex: 1;
`;

const AuthorName = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const PostTime = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
`;

const VisBadge = styled.View<{ variant: "public" | "internal" }>`
  border-radius: ${radius.full}px;
  padding-left: 8px;
  padding-right: 8px;
  padding-top: 3px;
  padding-bottom: 3px;
  border-width: 1px;
  background-color: ${(p) => (p.variant === "public" ? colors.brand50 : colors.bg)};
  border-color: ${(p) => (p.variant === "public" ? colors.brandLight : colors.line)};
`;

const VisBadgeText = styled.Text<{ variant: "public" | "internal" }>`
  font-size: 10px;
  font-weight: ${font.bold};
  color: ${(p) => (p.variant === "public" ? colors.brandDark : colors.inkMuted)};
`;

const PostText = styled.Text`
  font-size: 14px;
  color: ${colors.ink};
  line-height: 22px;
`;

const PostActions = styled.View`
  padding-left: 14px;
  padding-right: 14px;
  padding-top: 10px;
  padding-bottom: 10px;
  border-top-width: 1px;
  border-top-color: ${colors.line};
  flex-direction: row;
  gap: 20px;
`;

const ActionBtn = styled.Pressable`
  flex-direction: row;
  align-items: center;
  gap: 5px;
`;

const ActionCount = styled.Text`
  font-size: 12px;
  font-weight: ${font.bold};
  color: ${colors.inkMuted};
`;

const EmptyCard = styled.View`
  margin-left: 20px;
  margin-right: 20px;
  margin-top: 8px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border-width: 1px;
  border-color: ${colors.line};
  padding: 24px;
  align-items: center;
`;

const EmojiSmall = styled.Text`
  font-size: 14px;
`;

const DeleteText = styled.Text`
  font-size: 12px;
  font-weight: ${font.bold};
  color: ${colors.danger};
`;

const DeleteBtn = styled.Pressable`
  flex-direction: row;
  align-items: center;
  gap: 5px;
  margin-left: auto;
`;

const Spacer = styled.View`
  height: 32px;
`;

const ActionCard = styled.View`
  margin: 8px 20px;
  background-color: ${colors.ink};
  border-radius: ${radius.lg}px;
  padding: 14px;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const ActionCardEmoji = styled.Text`
  font-size: 24px;
`;

const ActionCardInfo = styled.View`
  flex: 1;
`;

const ActionCardTitle = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: #fff;
`;

const ActionCardSub = styled.Text`
  font-size: 12px;
  color: rgba(255,255,255,0.7);
  font-weight: ${font.bold};
  margin-top: 2px;
`;

const SelectionCard = styled.View`
  margin: 8px 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border-left-width: 4px;
  border-left-color: ${colors.brand};
  border: 1px solid ${colors.line};
  border-left-width: 4px;
  border-left-color: ${colors.brand};
  padding: 14px;
`;

const SelectionCardTitle = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const SelectionCardSub = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
  margin-top: 4px;
`;

const NudgeText = styled.Text`
  font-size: 13px;
  color: ${colors.amber};
  font-weight: ${font.bold};
  margin-top: 8px;
`;

const WeekCard = styled.View`
  margin: 8px 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border: 2px solid ${colors.blue200};
  overflow: hidden;
`;

const WeekCardHeader = styled.View`
  background-color: ${colors.blue50};
  padding: 12px 16px;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const WeekCardHeaderText = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.blue700};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const BlueDot = styled.View`
  width: 8px;
  height: 8px;
  border-radius: 4px;
  background-color: ${colors.blue700};
`;

const WeekMatchRow = styled.View<{ bordered?: boolean }>`
  flex-direction: row;
  align-items: center;
  padding: 12px 16px;
  ${(p) => p.bordered ? `border-bottom-width: 1px; border-bottom-color: ${colors.line};` : ""}
`;

const WeekMatchTeam = styled.Text`
  font-size: 12px;
  font-weight: ${font.bold};
  color: ${colors.inkMuted};
  width: 80px;
`;

const WeekMatchOpponent = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.ink};
  flex: 1;
`;

const VenuePill = styled.View<{ isHome: boolean }>`
  padding: 3px 8px;
  border-radius: 999px;
  background-color: ${(p) => p.isHome ? colors.brand50 : colors.bg};
  border: 1px solid ${(p) => p.isHome ? colors.brandLight : colors.line};
`;

const VenuePillText = styled.Text<{ isHome: boolean }>`
  font-size: 10px;
  font-weight: ${font.bold};
  color: ${(p) => p.isHome ? colors.brandDark : colors.inkMuted};
  text-transform: uppercase;
`;

const WeekCardFooter = styled.View`
  padding: 10px 16px;
  background-color: ${colors.bg};
  align-items: center;
`;

const EventCard = styled.View`
  margin: 8px 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.line};
  padding: 14px 16px;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const EventEmoji = styled.Text`
  font-size: 24px;
`;

const EventInfo = styled.View`
  flex: 1;
`;

const EventTitle = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const EventMeta = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
  margin-top: 2px;
`;

const EventFeePill = styled.View`
  background-color: ${colors.amber50};
  border-radius: 999px;
  padding: 3px 8px;
  border: 1px solid ${colors.amber200};
`;

const EventFeeText = styled.Text`
  font-size: 10px;
  font-weight: ${font.bold};
  color: ${colors.amber};
`;

const WeekCardFooterText = styled.Text`
  font-size: 12px;
  font-weight: ${font.bold};
  color: ${colors.blue700};
`;

/* ── Component ── */

export default function HomeScreen() {
  const { user } = useAuth();
  const { club, membership, loading: clubLoading } = useClub();
  const { matches } = useMatches(club?.id);
  const { upcoming: upcomingEvents } = useEvents(club?.id);
  const { posts, loading: postsLoading, loadingMore, hasMore, loadMore } = usePosts(club?.id);
  const [text, setText] = useState("");
  const [visibility, setVisibility] = useState<PostVisibility>("internal");
  const [saving, setSaving] = useState(false);
  const [composeFocused, setComposeFocused] = useState(false);

  const router = useRouter();
  const { confirmProps, confirm } = useConfirm();
  const availMap = useMyAvailability(club?.id, user?.uid);
  const [availSaving, setAvailSaving] = useState(false);
  const { teams: teamConfigs, getTeamConfig, getNextTeamInCascade, isCaptainOf, isAnyCaptain } = useTeamConfig(club?.id);
  const canDelete = membership?.role === "admin" || isAnyCaptain(user?.uid);
  const upcomingMatches = matches.filter((m) => m.status !== "completed");
  const nextMatch = upcomingMatches[0];

  // This week's matches
  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay() + 1); // Monday
  startOfWeek.setHours(0, 0, 0, 0);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6); // Sunday
  endOfWeek.setHours(23, 59, 59, 999);

  const thisWeekAll = upcomingMatches.filter((m) => {
    try {
      const d = new Date(m.date + "T00:00:00");
      return d >= startOfWeek && d <= endOfWeek;
    } catch { return false; }
  });
  // Show max 3, prioritise by team order (1st XI, 2nd XI, etc.)
  const teamOrder = club?.teams || [];
  const thisWeekMatches = thisWeekAll
    .sort((a, b) => {
      const ai = teamOrder.indexOf(a.team);
      const bi = teamOrder.indexOf(b.team);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    })
    .slice(0, 3);

  // Group this week's matches by date for availability
  const thisWeekDays: { date: string; label: string; matches: typeof thisWeekAll }[] = [];
  for (const m of thisWeekAll) {
    const existing = thisWeekDays.find((d) => d.date === m.date);
    if (existing) {
      existing.matches.push(m);
    } else {
      const d = new Date(m.date + "T00:00:00");
      thisWeekDays.push({
        date: m.date,
        label: d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }),
        matches: [m],
      });
    }
  }
  thisWeekDays.sort((a, b) => a.date.localeCompare(b.date));

  const handleDayAvail = async (date: string, matchIds: string[], status: AvailabilityStatus) => {
    if (!club || !user || !membership) return;
    setAvailSaving(true);
    try {
      await setDayAvailability(club.id, matchIds, {
        userId: user.uid, displayName: membership.displayName,
        status, note: "", clubId: club.id, clubName: club.name, matchDate: date,
      });
    } catch {} finally { setAvailSaving(false); }
  };

  // Find matches that need captain action (selected by previous team, waiting on next)
  const matchesNeedingSelection = matches.filter((m) => {
    if (m.status !== "selected") return false;
    const nextTeam = getNextTeamInCascade(m.team);
    if (!nextTeam) return false;
    // Check if the next team's match on same date exists and is not yet selected
    const nextMatch = matches.find(
      (nm) => nm.team === nextTeam.name && nm.date === m.date && nm.status !== "selected" && nm.status !== "completed"
    );
    return !!nextMatch;
  });

  // Am I a captain who needs to select?
  const mySelectionActions = matches.filter((m) => {
    if (m.status === "selected" || m.status === "completed") return false;
    if (!user) return false;
    return isCaptainOf(m.team, user.uid);
  });
  const likedSet = useLikedPosts(club?.id, posts.map((p) => p.id), user?.uid);

  const handleLike = async (postId: string) => {
    if (!club || !user || !membership) return;
    await toggleLike(club.id, postId, user.uid, membership.displayName, likedSet.has(postId));
  };

  const handlePost = async () => {
    if (!club || !user || !membership || !text.trim()) return;
    setSaving(true);
    await createPost(club.id, {
      authorId: user.uid,
      authorName: membership.displayName,
      authorRole: membership.role,
      text: text.trim(),
      visibility,
      clubName: club.name,
    });
    setSaving(false);
    setText("");
    setComposeFocused(false);
  };

  const handleDelete = async (postId: string) => {
    if (!club) return;
    const ok = await confirm({
      title: "Delete post?",
      message: "This cannot be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (ok) deletePost(club.id, postId);
  };

  if (clubLoading || !club) {
    return <View style={{ flex: 1, backgroundColor: colors.bg }}><FeedSkeleton /></View>;
  }

  return (
    <KeyboardAwareScroll
      style={{ flex: 1, backgroundColor: colors.bg }}
      showsVerticalScrollIndicator={false}
      onScroll={({ nativeEvent }: any) => {
        const { layoutMeasurement, contentOffset, contentSize } = nativeEvent;
        const distanceFromBottom = contentSize.height - (contentOffset.y + layoutMeasurement.height);
        if (distanceFromBottom < 400 && hasMore && !loadingMore) loadMore();
      }}
      scrollEventThrottle={200}
    >
      <ContentWidth>
      {/* Greeting */}
      {membership?.displayName && (
        <Section>
          <Greeting>Welcome back, {membership.displayName.split(" ")[0]}</Greeting>
        </Section>
      )}

      {/* Inline compose — always on top */}
      <ComposeCard focused={composeFocused}>
        <ComposeTop>
          <Avatar>
            <AvatarText>{membership?.displayName?.[0]?.toUpperCase() || "?"}</AvatarText>
          </Avatar>
          <ComposeInput
            placeholder="What's happening at the club?"
            placeholderTextColor={colors.inkMuted}
            value={text}
            onChangeText={setText}
            onFocus={() => setComposeFocused(true)}
            multiline
          />
        </ComposeTop>
        {(composeFocused || text.length > 0) && (
          <ComposeActions>
            <View />
            <PostBtn
              onPress={handlePost}
              disabled={saving || !text.trim()}
            >
              {saving ? <ActivityIndicator color="#fff" size="small" /> : <PostBtnText>Post</PostBtnText>}
            </PostBtn>
          </ComposeActions>
        )}
      </ComposeCard>

      {/* Availability for this week */}
      {thisWeekDays.length > 0 && (
        <WeekCard>
          <WeekCardHeader>
            <BlueDot />
            <WeekCardHeaderText>Your availability</WeekCardHeaderText>
          </WeekCardHeader>
          {thisWeekDays.map((day, i) => {
            const statuses = day.matches.map((m) => availMap.get(m.id) || null);
            const allSame = statuses.every((s) => s === statuses[0]);
            const dayStatus = allSame ? statuses[0] : null;
            const matchIds = day.matches.map((m) => m.id);

            return (
              <View key={day.date} style={{ borderTopWidth: i === 0 ? 0 : 1, borderTopColor: colors.line, padding: 12 }}>
                <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <Text style={{ fontSize: 14, fontWeight: "700", color: colors.ink }}>{day.label}</Text>
                  <Text style={{ fontSize: 11, color: colors.inkMuted }}>
                    {day.matches.length} match{day.matches.length !== 1 ? "es" : ""}
                  </Text>
                </View>
                <View style={{ flexDirection: "row", gap: 8 }}>
                  {(["available", "unavailable", "maybe"] as AvailabilityStatus[]).map((s) => {
                    const active = dayStatus === s;
                    const c = s === "available"
                      ? { bg: colors.brand50, border: colors.brandLight, text: colors.brand }
                      : s === "unavailable"
                      ? { bg: colors.dangerLight, border: "#fecaca", text: colors.danger }
                      : { bg: colors.amber50, border: colors.amber200, text: colors.amber };
                    return (
                      <Pressable
                        key={s}
                        onPress={() => handleDayAvail(day.date, matchIds, s)}
                        disabled={availSaving}
                        style={({ pressed }) => ({
                          flex: 1, paddingVertical: 8, borderRadius: radius.md,
                          alignItems: "center" as const,
                          backgroundColor: active ? c.bg : colors.bg,
                          borderWidth: 1.5,
                          borderColor: active ? c.border : colors.line,
                          opacity: pressed ? 0.7 : availSaving ? 0.5 : 1,
                        })}
                      >
                        <Text style={{ fontSize: 12, fontWeight: "700", color: active ? c.text : colors.inkMuted }}>
                          {s === "available" ? "Available" : s === "unavailable" ? "Unavailable" : "Maybe"}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              </View>
            );
          })}
        </WeekCard>
      )}

      {/* This week's matches — single compact card */}
      {thisWeekMatches.length > 0 && (
        <Pressable onPress={() => router.push("/(app)/(tabs)/matches")}>
          <WeekCard>
            <WeekCardHeader>
              <BlueDot />
              <WeekCardHeaderText>This week · {thisWeekAll.length} match{thisWeekAll.length !== 1 ? "es" : ""}</WeekCardHeaderText>
            </WeekCardHeader>
            {thisWeekMatches.map((m, i) => (
              <Pressable key={m.id} onPress={() => router.push(`/(app)/match-detail?id=${m.id}`)}>
                <WeekMatchRow bordered={i < thisWeekMatches.length - 1}>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 14, color: colors.ink }}>
                      <Text style={{ fontWeight: "700" }}>{shortTeamName(m.team)}</Text>
                      <Text style={{ fontWeight: "500", color: colors.inkMuted }}> vs </Text>
                      <Text style={{ fontWeight: "700" }}>{shortOpponentName(m.opponent)}</Text>
                    </Text>
                    <Text style={{ fontSize: 11, color: colors.inkMuted, marginTop: 2 }}>
                      {new Date(m.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
                      {m.time ? ` · ${m.time}` : ""} · {m.venue === "home" ? "Home" : "Away"}
                    </Text>
                  </View>
                </WeekMatchRow>
              </Pressable>
            ))}
            <WeekCardFooter>
              <WeekCardFooterText>View all matches →</WeekCardFooterText>
            </WeekCardFooter>
          </WeekCard>
        </Pressable>
      )}

      {/* Captain selection actions */}
      {mySelectionActions.map((m) => (
        <Pressable key={`action-${m.id}`} onPress={() => router.push(`/(app)/team-selection?matchId=${m.id}`)}>
          <ActionCard>
            <ActionCardEmoji>🏏</ActionCardEmoji>
            <ActionCardInfo>
              <ActionCardTitle>Select your {m.team} for {m.opponent}</ActionCardTitle>
              <ActionCardSub>{formatDate(m.date)} · Action on you, captain!</ActionCardSub>
            </ActionCardInfo>
          </ActionCard>
        </Pressable>
      ))}

      {/* Confirmed selections with cascade nudge */}
      {matches.filter((m) => m.status === "selected").map((m) => {
        const nextTeam = getNextTeamInCascade(m.team);
        const nextTeamMatch = nextTeam ? matches.find(
          (nm) => nm.team === nextTeam.name && nm.date === m.date && nm.status !== "selected" && nm.status !== "completed"
        ) : null;

        return (
          <SelectionCard key={`sel-${m.id}`}>
            <SelectionCardTitle>🏏 {m.team} vs {m.opponent} — Team confirmed</SelectionCardTitle>
            <SelectionCardSub>{formatDate(m.date)} · {formatTime(m.time)} · {m.venue}</SelectionCardSub>
            {nextTeamMatch && nextTeam?.captainName && (
              <NudgeText>Action on you, {nextTeam.captainName} 👀</NudgeText>
            )}
          </SelectionCard>
        );
      })}

      {/* Upcoming events */}
      {upcomingEvents.slice(0, 3).map((evt) => (
        <Pressable key={evt.id} onPress={() => router.push(`/(app)/event-detail?id=${evt.id}`)}>
          <EventCard>
            <EventEmoji>📅</EventEmoji>
            <EventInfo>
              <EventTitle>{evt.title}</EventTitle>
              <EventMeta>{formatDate(evt.date)} · {evt.location}</EventMeta>
            </EventInfo>
            {evt.isPaid && evt.fee > 0 && (
              <EventFeePill>
                <EventFeeText>£{(evt.fee / 100).toFixed(0)}</EventFeeText>
              </EventFeePill>
            )}
          </EventCard>
        </Pressable>
      ))}

      {/* Posts */}
      {posts.map((post) => (
        <Pressable key={post.id} onPress={() => router.push(`/(app)/post-detail?id=${post.id}`)}>
        <PostCard>
          <PostBody>
            <AuthorRow>
              <AuthorAvatar>
                <AuthorAvatarText>{post.authorName?.[0]?.toUpperCase()}</AuthorAvatarText>
              </AuthorAvatar>
              <AuthorNameFlex>
                <AuthorName>{post.authorName}</AuthorName>
                <PostTime>{timeAgo(post.createdAt)}</PostTime>
              </AuthorNameFlex>
            </AuthorRow>
            <PostText>{post.text}</PostText>
          </PostBody>
          <PostActions>
            <ActionBtn onPress={() => handleLike(post.id)}>
              <EmojiSmall>{likedSet.has(post.id) ? "❤️" : "🤍"}</EmojiSmall>
              <ActionCount>{post.likeCount}</ActionCount>
            </ActionBtn>
            <ActionBtn onPress={() => router.push(`/(app)/post-detail?id=${post.id}`)}>
              <EmojiSmall>💬</EmojiSmall>
              <ActionCount>{post.commentCount}</ActionCount>
            </ActionBtn>
            {canDelete && (
              <DeleteBtn onPress={() => handleDelete(post.id)}>
                <DeleteText>Delete</DeleteText>
              </DeleteBtn>
            )}
          </PostActions>
        </PostCard>
        </Pressable>
      ))}

      {posts.length === 0 && !composeFocused && (
        <EmptyCard>
          <EmptyText>No posts yet. Be the first to share something!</EmptyText>
        </EmptyCard>
      )}

      {loadingMore && (
        <View style={{ paddingVertical: 20, alignItems: "center" }}>
          <ActivityIndicator color={colors.brand} />
        </View>
      )}
      {!hasMore && posts.length > 20 && (
        <View style={{ paddingVertical: 16, alignItems: "center" }}>
          <Text style={{ fontSize: 12, color: colors.inkMuted }}>That's everything 🏏</Text>
        </View>
      )}

      <Spacer />
      </ContentWidth>
      <ConfirmSheet {...confirmProps} />
    </KeyboardAwareScroll>
  );
}
